/**
 * Phoneme Capture Hook
 *
 * Captures user audio and compares against reference phoneme files.
 * All processing is CLIENT-SIDE using Web Audio API.
 * No server roundtrip - instant feedback.
 *
 * Accepts an optional external MediaStream from useMicStream. When provided,
 * creates a MediaRecorder from the shared stream and does NOT stop its
 * tracks on cleanup (the stream owner handles lifecycle).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { comparePhoneme, preloadReferences, type PhonemeComparisonResult } from '../audio-embeddings';

export type { PhonemeComparisonResult } from '../audio-embeddings';

interface UsePhonemeCapture {
  isCapturing: boolean;
  startCapture: (externalStream?: MediaStream | null) => Promise<void>;
  stopCapture: () => Promise<Blob | null>;
  verifyPhoneme: (phoneme: string, audioBlob: Blob) => Promise<PhonemeComparisonResult>;
  preload: (phoneme: string) => Promise<void>;
  lastResult: PhonemeComparisonResult | null;
  error: string | null;
}

export function usePhonemeCapture(): UsePhonemeCapture {
  const [isCapturing, setIsCapturing] = useState(false);
  const [lastResult, setLastResult] = useState<PhonemeComparisonResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isExternalRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);

  const startCapture = useCallback(async (externalStream?: MediaStream | null) => {
    try {
      setError(null);
      audioChunksRef.current = [];

      let stream: MediaStream;

      if (externalStream) {
        stream = externalStream;
        isExternalRef.current = true;
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        isExternalRef.current = false;
      }

      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(50);
      setIsCapturing(true);
    } catch (err) {
      console.error('[PhonemeCapture] Failed to start:', err);
      setError('Could not access microphone');
    }
  }, []);

  const stopCapture = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        setIsCapturing(false);
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        if (!isExternalRef.current) {
          streamRef.current?.getTracks().forEach(track => track.stop());
        }
        streamRef.current = null;
        mediaRecorderRef.current = null;

        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        audioChunksRef.current = [];

        setIsCapturing(false);
        resolve(audioBlob);
      };

      mediaRecorder.stop();
    });
  }, []);

  const verifyPhoneme = useCallback(async (
    phoneme: string,
    audioBlob: Blob
  ): Promise<PhonemeComparisonResult> => {
    try {
      setError(null);

      if (audioBlob.size < 1000) {
        const result: PhonemeComparisonResult = {
          isCorrect: false,
          similarity: 0,
          threshold: 0.5,
          phoneme,
          hasReference: false,
          feedback: 'I couldn\'t hear you. Try speaking louder.',
        };
        setLastResult(result);
        return result;
      }

      const result = await comparePhoneme(audioBlob, phoneme);
      setLastResult(result);
      return result;
    } catch (err) {
      console.error('[PhonemeCapture] Comparison error:', err);
      setError('Could not compare audio');

      const fallback: PhonemeComparisonResult = {
        isCorrect: false,
        similarity: 0,
        threshold: 0.5,
        phoneme,
        hasReference: false,
        feedback: 'Could not analyze. Try again.',
      };
      setLastResult(fallback);
      return fallback;
    }
  }, []);

  const preload = useCallback(async (phoneme: string) => {
    await preloadReferences([phoneme]);
  }, []);

  // Cleanup orphaned MediaRecorder on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (!isExternalRef.current && streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      streamRef.current = null;
      mediaRecorderRef.current = null;
    };
  }, []);

  return {
    isCapturing,
    startCapture,
    stopCapture,
    verifyPhoneme,
    preload,
    lastResult,
    error,
  };
}
